'use client'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Dictionary } from '@/localization/dictionary'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { forgotPasswordAction } from './forgot-password.actions'
import useSafeAction from '@/lib/safe-action/use-safe-action'
import { showToast } from '@/lib/utils/toast'
import { forgotPasswordSchema } from '@/lib/api/shopware/account/account.schemas'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import Text from '@/components/custom-ui/text'
import DefaultLoadingSpinner from '@/components/custom-ui/loading-spinners/default-loading-spinner'

interface ForgotPasswordProps {
  dict: Dictionary['forgotPasswordForm']
  onBackToLogin: () => void
}

const ForgotPassword = ({ dict, onBackToLogin }: ForgotPasswordProps) => {
  const form = useForm<z.infer<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  const { execute, isExecuting } = useSafeAction(forgotPasswordAction, {
    onSuccess: () => {
      showToast(dict.alert.success)
    },
  })

  return (
    <>
      <Text
        as="h2"
        size={31}
        lineHeight={34.27}
        uppercase
        markdown
        className="mb-7.5 text-center font-normal text-secondary-900"
      >
        {dict.title}
      </Text>

      <Text as="p" className="mb-5" size={16} lineHeight={24}>
        {dict.subtitle}
      </Text>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(execute)}
          className="flex w-full flex-col gap-5"
          noValidate
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="grid w-full items-center">
                <FormLabel className="text-xs font-normal">
                  {dict.email}
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={dict.email}
                    className="h-auto rounded-none px-4 py-3.5 text-base"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            onClick={onBackToLogin}
            variant="plain"
            size="plain"
            className="w-fit"
            type="button"
          >
            <Text
              as="p"
              underline
              className="w-fit border-primary-600 text-primary-600"
            >
              {dict.backToLogin}
            </Text>
          </Button>

          <Button type="submit">
            {isExecuting ? (
              <DefaultLoadingSpinner />
            ) : (
              <Text as="p" uppercase>
                {dict.sendLink}
              </Text>
            )}
          </Button>
        </form>
      </Form>
    </>
  )
}

export default ForgotPassword
